import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import qs from 'query-string';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';

import DocketsFilters from './components/DocketsFilters/DocketsFilters';
import DocketsTable from './components/DocketsTable/DocketsTable';
import Pagination from '@components/Pagination/Pagination';
import Search from '@components/Search/Search';

import {
  getCaseDockets,
  getCaseDocketsFilters,
} from '@actions/caseDockets';

import {
  downloadAllDocketsCSV,
  downloadDocketsCSV,
} from '@actions/export';

import {
  docketsClaimList,
  docketsCount,
  docketsList,
  docketsTypes,
} from '@selectors/caseDockets';

import { isGreaterThanTablet } from '@selectors/browser';

import './CaseDockets.scss';

class CaseDockets extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      searchQuery: '',
      filterValues: {},
      checkedDockets: [],
    };
    this.debounceId = null;
    const { search } = this.props.location;
    const { claimId } = qs.parse(search);

    if (claimId) this.state.filterValues.claimId = Number(claimId);

    this.setFilterQuery = this.setFilterQuery.bind(this);
    this.setSearchQuery = this.setSearchQuery.bind(this);
    this.fetchCaseDockets = this.fetchCaseDockets.bind(this);
    this.setPage = this.setPage.bind(this);
    this.setCheckedDockets = this.setCheckedDockets.bind(this);
    this.onExportClick = this.onExportClick.bind(this);
    this.downloadAllDocketsCSV = this.downloadAllDocketsCSV.bind(this);
  }

  componentDidMount() {
    const {
      fetchCaseDocketsFilters,
      match: { params: { id } },
    } = this.props;

    fetchCaseDocketsFilters(id);
    this.fetchCaseDockets(id);
  }

  onExportClick() {
    const {
      downloadDocketsCSV,
      match: { params: { id } },
    } = this.props;

    const { checkedDockets } = this.state;

    downloadDocketsCSV(id, checkedDockets);
  }

  setCheckedDockets([id, ...ids]) {
    const { checkedDockets } = this.state;
    if (ids.length) {
      this.setState({
        checkedDockets: checkedDockets.length !== ids.length + 1
          ? [id, ...ids]
          : [],
      });
    } else {
      this.setState({
        checkedDockets: checkedDockets.includes(id)
          ? checkedDockets.filter(checkedId => checkedId !== id)
          : [...checkedDockets, id],
      });
    }
  }

  setSearchQuery({ target: { value } }) {
    this.setState({
      searchQuery: value,
      page: 1,
      checkedDockets: [],
    }, () => {
      clearTimeout(this.debounceId);
      this.debounceId = setTimeout(this.fetchCaseDockets, 500);
    });
  }

  setPage(page) {
    this.setState({
      page,
      checkedDockets: [],
    }, this.fetchCaseDockets);
  }

  setFilterQuery(filterValues) {
    this.setState({
      filterValues,
      page: 1,
      checkedDockets: [],
    }, this.fetchCaseDockets);
  }

  fetchCaseDockets() {
    const {
      fetchCaseDockets,
      match: { params: { id } },
    } = this.props;

    const {
      filterValues,
      page,
      searchQuery,
    } = this.state;

    fetchCaseDockets(id, {
      ...filterValues,
      page,
      search: searchQuery,
    });
  }

  downloadAllDocketsCSV() {
    const {
      downloadAllDocketsCSV,
      match: { params: { id } },
    } = this.props;

    const {
      filterValues,
      searchQuery,
    } = this.state;

    downloadAllDocketsCSV(id, {
      ...filterValues,
      search: searchQuery,
    });
  }

  render() {
    const {
      docketsClaimList,
      docketsList,
      docketsTypes,
      docketsCount,
      showExportButtons,
    } = this.props;

    const {
      filterValues,
      searchQuery,
      page,
      checkedDockets,
    } = this.state;

    return (
      <div className="case-dockets">
        <Paper className="case-dockets__search">
          <Search
            placeholder="Search for a docket..."
            searchQuery={searchQuery}
            setSearchQuery={this.setSearchQuery}
          />
        </Paper>
        <div className="case-dockets__filters">
          <DocketsFilters
            docketsClaimList={docketsClaimList}
            docketsTypes={docketsTypes}
            filterValues={filterValues}
            onSubmit={this.setFilterQuery}
            initialValues={{
              claimId: filterValues.claimId || '',
            }}
          />
        </div>
        {docketsList.length > 0 && (
          <div className="case-dockets__table">
            <Pagination
              page={page}
              count={docketsCount}
              onPageChange={this.setPage}
              bar={showExportButtons && (
                <div className="case-dockets__button-wrapper">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.onExportClick}
                    disabled={checkedDockets.length === 0}
                  >
                    Export to CSV
                  </Button>
                  <div className="case-dockets__separator">
                    or
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.downloadAllDocketsCSV}
                  >
                    Export all to CSV
                  </Button>
                </div>
              )}
            >
              <DocketsTable
                docketsList={docketsList}
                checkedDockets={checkedDockets}
                onCheck={this.setCheckedDockets}
                enableExport={showExportButtons}
              />
            </Pagination>
          </div>
        )}
        {docketsList.length === 0 && (
          <Paper className="case-dockets__empty-message">
            There are no dockets to display.
          </Paper>
        )}
      </div>
    );
  }
}

CaseDockets.propTypes = {
  docketsClaimList: PropTypes.arrayOf(PropTypes.object).isRequired,
  docketsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  docketsCount: PropTypes.number.isRequired,
  docketsTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  downloadAllDocketsCSV: PropTypes.func.isRequired,
  downloadDocketsCSV: PropTypes.func.isRequired,
  fetchCaseDockets: PropTypes.func.isRequired,
  fetchCaseDocketsFilters: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  showExportButtons: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  docketsClaimList: docketsClaimList(state),
  docketsList: docketsList(state),
  docketsCount: docketsCount(state),
  docketsTypes: docketsTypes(state),
  showExportButtons: isGreaterThanTablet(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  downloadAllDocketsCSV,
  downloadDocketsCSV,
  fetchCaseDockets: getCaseDockets,
  fetchCaseDocketsFilters: getCaseDocketsFilters,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CaseDockets);



// WEBPACK FOOTER //
// ./src/routes/Case/Dockets/CaseDockets.js